import React, { Fragment, useEffect, useState } from 'react';

import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { CodeType, LicenseReturnable, TransactionApi } from 'apis/TransactionApi';
import { chunkArray, getMostRestrictedLicenseDeliverable } from 'utils/objects';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { TransactionType } from 'types';

import 'components/NewGroupForm/newGroupForm.scss';

interface NewAccessCodeProps {
    ActionsWrapper?: React.ElementType;
    ContentWrapper?: React.ElementType;
    newAccessCodeData: any;
    onClose?: () => void;
    onSuccess?: (accessCodeId: number) => void;
}

const NewAccessCodeForm = ({ ActionsWrapper = Fragment, ContentWrapper = Fragment, newAccessCodeData, onClose, onSuccess }: NewAccessCodeProps) => {

    const { formatMessage } = useIntl();
    const { catchWithToast, createToast } = useNotification();

    const [accessCodeName, setAccessCodeName] = useState('');
    const [maxUses, setMaxUses] = useState<number | ''>('');
    const [licenses, setLicenses] = useState<LicenseReturnable>();
    const [isInvalidProps, setIsInvalideProps] = useState({ name: { message: '', validType: '' }, identifier: { message: '', validType: '' }, maxUses: { message: '', validType: '' } });
    const [codeIdentifier, setCodeIdentifier] = useState('');
    const [accessCodeType, setAccessCodeType] = useState({ id: CodeType.Multiple, label: formatMessage({ id: 'multi_user_one_code_for_all_redemptions.1cfad3b2', defaultMessage: 'Multi-User: One code for all redemptions' }) });
    const [enableBulb, setEnableBulb] = useState(false);
    const [enableCloud, setEnableCloud] = useState(false);
    const [loadingSaveButton, setLoadingSaveButton] = useState(false);

    useEffect(() => {

        getLicenses();

    }, []);

    const getLicenses = async () => {

        try {

            const licenses = await TransactionApi.GetAllLicenses();
            if (licenses) setLicenses(licenses);

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'failed_to_load_license_data.ccdc93b9', defaultMessage: 'Failed to load license data.' }));

        }

    };

    const createNewAccessCode = async () => {

        setLoadingSaveButton(true);

        const invalidProps = { name: { message: '', validType: '' }, identifier: { message: '', validType: '' }, maxUses: { message: '', validType: '' } };

        if (!maxUses) {

            invalidProps.maxUses = { message: formatMessage({ id: 'please_enter_a_number_for_available_uses.4e8a5d51', defaultMessage: 'Please enter a number for Available Uses.' }), validType: 'invalid' };

        } else if (maxUses > 999) {

            invalidProps.maxUses = { message: formatMessage({ id: 'available_uses_may_not_exceed_999.02acd8cf', defaultMessage: 'Available Uses may not exceed 999.' }), validType: 'invalid' };

        } else if (maxValueForAvailableUses && maxUses > maxValueForAvailableUses) {

            invalidProps.maxUses = { message: `You only have ${maxValueForAvailableUses} uses available and may not exceed that amount.`, validType: 'invalid' };

        }

        if (accessCodeName === '') {

            invalidProps.name = { message: formatMessage({ id: 'this_is_a_required_field.a7314606', defaultMessage: 'This is a required field.' }), validType: 'invalid' };

        }
        setIsInvalideProps(invalidProps);
        if (invalidProps.name.validType != '' || invalidProps.identifier.validType != '' || invalidProps.maxUses.validType != '') {

            setLoadingSaveButton(false);

            return;

        }
        const restrictedLicense = getMostRestrictedLicenseDeliverable(licenses?.licenses ?? [], newAccessCodeData.content);

        if (licenses?.licenses.length === 0) {

            //error this shouldnt happen
            createToast(formatMessage({ id: 'error_getting_license_info_please_contac.bd41a56e', defaultMessage: 'Error getting license info, please contact GMetrix support' }), 'danger');
            setLoadingSaveButton(false);

            return;

        }

        let createdId = 0;

        try {

            const result = await TransactionApi.Create({
                ...newAccessCodeData.transactionDetails,
                HasBulbAccess: enableBulb,
                HasCloud: enableCloud,
                Identifier: codeIdentifier,
                LeastOrderExpirationDate: restrictedLicense.license.OrderExpireDateString,
                Name: accessCodeName,
                TransactionType: TransactionType.Assignment,
                UsesRemaining: maxUses
            });

            if (!result || !result.Done || !result.Id) {

                createToast(result?.Message || formatMessage({ id: 'error_creating_access_code_please_try_ag.de4df4f3', defaultMessage: 'Error creating access code, please try again' }), 'danger');
                setLoadingSaveButton(false);

                return;

            }

            createdId = result.Id;

            const timeDifference = new Date(restrictedLicense.license.OrderExpireDate).getTime() - new Date().getTime();
            const dayDifference = timeDifference / (1000 * 3600 * 24);

            await TransactionApi.CreateAccessCodeForTransaction(
                result.Id,
                codeIdentifier,
                accessCodeType.id,
                Math.round(dayDifference),
                restrictedLicense.license.OrderExpireDate,
                maxUses,
                accessCodeType.id === CodeType.Single ? true : false
            );

            let chunkedTestArray: any[] = [];
            let chunkedBookArray: any[] = [];
            let chunkedCourseArray: any[] = [];
            let chunkUpload = false;

            if (newAccessCodeData.transactionDetails.PendingTests.length > 100 ||
                newAccessCodeData.transactionDetails.PendingBooks.length > 100 ||
                newAccessCodeData.transactionDetails.PendingCourses.length > 100
            ) {

                chunkedTestArray = chunkArray(newAccessCodeData.transactionDetails.PendingTests, 20);
                chunkedBookArray = chunkArray(newAccessCodeData.transactionDetails.PendingBooks, 20);
                chunkedCourseArray = chunkArray(newAccessCodeData.transactionDetails.PendingCourses, 20);
                chunkUpload = true;

            }
            if (chunkUpload) {

                let maxAmount = chunkedTestArray.length;
                if (chunkedBookArray.length > maxAmount) {

                    maxAmount = chunkedBookArray.length;

                }
                if (chunkedCourseArray.length > maxAmount) {

                    maxAmount = chunkedCourseArray.length;

                }
                for (let index = 0; index < maxAmount; index++) {

                    await TransactionApi.AddContentToTransaction(
                        result.Id,
                        chunkedTestArray[index] ?? [],
                        chunkedBookArray[index] ?? [],
                        chunkedCourseArray[index] ?? []
                    );

                }

            } else {

                await TransactionApi.AddContentToTransaction(
                    result.Id,
                    newAccessCodeData.transactionDetails.PendingTests ?? [],
                    newAccessCodeData.transactionDetails.PendingBooks ?? [],
                    newAccessCodeData.transactionDetails.PendingCourses ?? []
                );

            }

        } catch (e) {

            catchWithToast(e, formatMessage({ id: 'error_creating_access_code_please_try_ag.de4df4f3', defaultMessage: 'Error creating access code, please try again' }));

        }
        setLoadingSaveButton(false);

        // The record exists once Create succeeds, so open it even if a follow-up step failed.
        if (createdId) onSuccess?.(createdId);

    };

    const handleAvailableUsesMaxValue = (accessCodeType: number) => {

        if (newAccessCodeData === undefined) {

            return;

        }
        const restrictedLicense = getMostRestrictedLicenseDeliverable(licenses?.licenses ?? [], newAccessCodeData.content);

        return restrictedLicense.avaliableStudentsAllowed === -1 ? 999 : Math.min(restrictedLicense.avaliableStudentsAllowed, 999);

    };

    const maxValueForAvailableUses = handleAvailableUsesMaxValue(accessCodeType.id);

    const selectedOrderIds = new Set((newAccessCodeData?.content ?? []).map((c: any) => c.RestrictedOrderId));
    const contentLicenses = licenses?.licenses?.filter(l => selectedOrderIds.has(l.OrderId)) ?? [];

    return (
        <>
            <ContentWrapper>
                <Container fluid className="details">
                    <Row className="d-flex mb-2">
                        <Col md={6}>
                            <GMetrixInput
                                autoFocus
                                required
                                onChange={e => {

                                    setAccessCodeName(e.target.value);

                                }}
                                feedbackMessage={isInvalidProps.name.message}
                                label={formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' })}
                                maxLength={50}
                                placeholder={formatMessage({ id: 'enter_access_code_name_eg_spreadsheets_1.fc932fe5', defaultMessage: 'Enter Access Code Name (e.g., Spreadsheets  101)' })}
                                validType={isInvalidProps.name.validType as any}
                                value={accessCodeName}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    setCodeIdentifier(e.target.value.replace(/[^a-zA-Z0-9]/g, ''));

                                }}
                                feedbackMessage={isInvalidProps.identifier.message}
                                infoTitle={formatMessage({ id: 'the_identifier_is_used_to_identify_the_c.88ee9066', defaultMessage: 'The identifier is used to identify the code. Additional random characters will be added when the access code is generated.' })}
                                label={formatMessage({ id: 'identifier_optional.6ca7cdc8', defaultMessage: 'Identifier (optional)' })}
                                maxLength={8}
                                placeholder={formatMessage({ id: 'max_8_character_code_letters_and_numbers.a31ff703', defaultMessage: 'Max 8-character code (letters & numbers, e.g., OMC101A1)' })}
                                validType={isInvalidProps.identifier.validType as any}
                                value={codeIdentifier}
                            />
                        </Col>
                    </Row>
                    <Row className="d-flex mb-2">
                        <Col md={6}>
                            <GMetrixInput
                                onChange={e => {

                                    const rawValue = e.target.value;

                                    // allow empty string
                                    if (rawValue === '') {

                                        setMaxUses('');
                                        return;

                                    }

                                    // only allow digits
                                    if (!/^\d+$/.test(rawValue)) {

                                        return; // ignore non-numeric input

                                    }
                                    const parsed = parseInt(rawValue, 10);

                                    // allow values above the cap through so validation can surface the error
                                    if (parsed > 0) {

                                        setMaxUses(parsed);

                                    }

                                }}
                                feedbackMessage={isInvalidProps.maxUses.message}
                                infoTitle={formatMessage({ id: 'this_sets_a_cap_on_how_many_students_can.b6be4f68', defaultMessage: 'This sets a cap on how many students can join. Changing content later may require adjusting this limit if licenses do not have enough uses.' })}
                                inputMode="numeric"
                                label={formatMessage({ id: 'available_uses.28af4457', defaultMessage: 'Available Uses' })}
                                max={maxValueForAvailableUses}
                                min={1}
                                subLabel={maxValueForAvailableUses && maxValueForAvailableUses >= 999 ? '' : `(Max ${maxValueForAvailableUses})`}
                                type="number"
                                validType={isInvalidProps.maxUses.validType as any}
                                value={maxUses}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={type => {

                                    setAccessCodeType(type as any);

                                }}
                                dataItemKey="id"
                                dataItems={[{ id: CodeType.Multiple, label: formatMessage({ id: 'multi_user_one_code_for_all_redemptions.1cfad3b2', defaultMessage: 'Multi-User: One code for all redemptions' }) }, { id: CodeType.Single, label: formatMessage({ id: 'single_user_many_codes_with_one_redempti.461286f8', defaultMessage: 'Single-User: Many codes with one redemption each' }) }]}
                                label={formatMessage({ id: 'access_code_type.6da058fd', defaultMessage: 'Access Code Type' })}
                                textField="label"
                                value={accessCodeType}
                            />
                        </Col>
                    </Row>
                    {(contentLicenses.some(license => license.BulbAccessUses > 0) || contentLicenses.some(license => license.CloudAccessUses > 0)) && (
                        <Row className="mt-3 mb-2">
                            <Col>
                                <Form.Label className="m-0 fw-bold">
                                    <FormattedMessage defaultMessage="Additional Features" id="additional_features.22ca02d5" />
                                </Form.Label>
                                <div className="d-flex gap-4 mt-2">
                                    {contentLicenses.some(license => license.BulbAccessUses > 0) && (
                                        <GMetrixCheckInput
                                            checked={enableBulb}
                                            disabled={loadingSaveButton}
                                            infoTitle={formatMessage({ id: 'enables_bulb_access.67a4b911', defaultMessage: 'Enables bulb access' })}
                                            label={formatMessage({ id: 'enable_bulb_portfolio.a0c8d39b', defaultMessage: 'Enable bulb Portfolio' })}
                                            onChange={e => setEnableBulb(e.target.checked)}
                                            type="checkbox"
                                        />
                                    )}
                                    {contentLicenses.some(license => license.CloudAccessUses > 0) && (
                                        <GMetrixCheckInput
                                            checked={enableCloud}
                                            disabled={loadingSaveButton}
                                            infoTitle={formatMessage({ id: 'enables_cloud_access.1d170166', defaultMessage: 'Enables cloud access' })}
                                            label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                            onChange={e => setEnableCloud(e.target.checked)}
                                            type="checkbox"
                                        />
                                    )}
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Going to put this back soon */}
                    {/* <GMetrixCheckInput label={"Associate on first use"} type={"checkbox"} infoTitle="Associates on first use" checked={isFirstUse} onChange={e => setIsFirstUse(e.target.checked)} /> */}
                </Container>
            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={onClose} variant="secondary">
                        <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                    </GMetrixButton>
                )}
                <GMetrixButton disabled={loadingSaveButton} onClick={createNewAccessCode} variant="primary">
                    {loadingSaveButton ? <i className="fa-solid fa-spinner-third fa-spin" ></i> : <FormattedMessage defaultMessage="Create" id="create.be56b47d" />}
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};
NewAccessCodeForm.displayName = 'NewAccessCodeForm';

export default NewAccessCodeForm;